// Type definitions for field transformations
export type TransformType = 'int' | 'string' | 'float' | 'bool' | 'date';

export type TransformMap = Record<string, TransformType>;

// Transforms a value to the specified type
const transformValue = (value: unknown, type: TransformType): unknown => {
  if (value === null || value === undefined) {
    return undefined;
  }

  switch (type) {
    case 'int':
      return parseInt(String(value), 10);
    case 'string':
      return String(value);
    case 'float':
      return parseFloat(String(value));
    case 'bool':
      return Boolean(value);
    case 'date':
      return new Date(String(value)).toISOString();
    default:
      return value;
  }
};

// Universal transformer - transforms object properties based on a transformation map
export const transformByMap = <T extends Record<string, unknown>>(
  data: T | T[] | null | undefined,
  transformMap: TransformMap
): T | T[] | null | undefined => {
  // Handle null/undefined/non-array inputs
  if (!data?.length) {
    return data;
  }

  // Transform each item in the array
  return (data as Record<string, unknown>[]).map((item: unknown) => {
    if (typeof item !== 'object' || item === null) {
      return item;
    }

    const transformed = { ...item } as Record<string, unknown>;

    // Apply transformations based on the map
    Object.entries(transformMap).forEach(([key, type]) => {
      if (key in transformed) {
        transformed[key] = transformValue(transformed[key], type);
      }
    });

    return transformed;
  }) as T[];
};
